// ОТК.1: примитивы анимации для CrateOpeningScene.
//
// Портировано из docs/design/design_handoff_crate_opening/reference/animations.jsx.
// Чистый JS без зависимостей. Stage запускает requestAnimationFrame-цикл и
// раздаёт текущее время через React context. Sprite рендерится только в
// окне [start, end].
//
// Все easing — hand-rolled (см. Easing).

import React from 'react';
import { createPortal } from 'react-dom';

export const Easing = {
  linear: (t: number) => t,

  easeInQuad:    (t: number) => t * t,
  easeOutQuad:   (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),

  easeInCubic:    (t: number) => t * t * t,
  easeOutCubic:   (t: number) => { t = t - 1; return t * t * t + 1; },
  easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),

  easeInQuart:    (t: number) => t * t * t * t,
  easeOutQuart:   (t: number) => { t = t - 1; return 1 - t * t * t * t; },
  easeInOutQuart: (t: number) => {
    if (t < 0.5) return 8 * t * t * t * t;
    const t1 = t - 1;
    return 1 - 8 * t1 * t1 * t1 * t1;
  },

  easeOutBack: (t: number) => {
    const c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
};

export type EaseFn = (t: number) => number;

// Tween helper — основной примитив для всех анимаций сцены.
// T(time, startTime, endTime, fromValue, toValue, easing) → value
export function T(t: number, s: number, e: number, from: number, to: number, ease: EaseFn = Easing.easeInOutCubic): number {
  if (t <= s) return from;
  if (t >= e) return to;
  return from + (to - from) * ease((t - s) / (e - s));
}

// ── Timeline context ────────────────────────────────────────
interface TimelineCtx {
  time: number;       // секунды от старта
  duration: number;   // полная длительность сцены
  playing: boolean;
}
const TimelineContext = React.createContext<TimelineCtx>({ time: 0, duration: 10, playing: false });
export const useTime = () => React.useContext(TimelineContext).time;
export const useTimeline = () => React.useContext(TimelineContext);

interface StageProps {
  width: number;
  height: number;
  duration: number;
  playing?: boolean;
  onComplete?: () => void;
  children: React.ReactNode;
  background?: string;
  style?: React.CSSProperties;
}

// Stage — корень анимации. Держит RAF-loop, раздаёт время.
// Автоматически центрирует/скейлит viewport (letterbox) под размер контейнера.
export function Stage({
  width, height, duration,
  playing = true,
  onComplete,
  children,
  background = '#050509',
  style
}: StageProps) {
  const [time, setTime] = React.useState(0);
  const startRef = React.useRef<number | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const completedRef = React.useRef(false);
  const onCompleteRef = React.useRef(onComplete);
  React.useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  React.useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    completedRef.current = false;
    startRef.current = null;
    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const t = (ts - startRef.current) / 1000;
      const clamped = Math.min(t, duration);
      setTime(clamped);
      if (t < duration) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (!completedRef.current) {
        completedRef.current = true;
        onCompleteRef.current?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, duration]);

  // Letterbox: масштабируем 720×1280 под доступную область, сохраняя aspect.
  //
  // КРИТИЧЕСКИ ВАЖНО: используем ResizeObserver вместо useEffect+resize.
  // createPortal вставляет div в body АСИНХРОННО после рендера, и обычный
  // useEffect ловит getBoundingClientRect=0 до вставки. ResizeObserver
  // сработает в момент когда div получит реальные размеры → scale
  // пересчитается правильно. useLayoutEffect — чтобы установить до paint.
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState(0); // 0 = ждём первого замера
  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const sx = r.width / width;
      const sy = r.height / height;
      setScale(Math.min(sx, sy));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [width, height]);

  // Через portal в document.body, чтобы position:fixed не ломался
  // никакими transform/clip-path/contain на родителях (ScreenLayout
  // имеет clip-path-обрезку, TonConnect-провайдер может ставить transform).
  //
  // Центрирование канваса через absolute top:50%/left:50% +
  // translate(-50%, -50%). Это работает корректно с transform: scale
  // на ребёнке: flex/justify-content центрирует ПО CSS-размеру
  // ДО transform — на мобильном viewport 414px wide ребёнок 720px
  // получал отрицательный margin и точка origin плыла.
  const node = (
    <TimelineContext.Provider value={{ time, duration, playing }}>
      <div
        ref={wrapRef}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          width: '100vw', height: '100vh',
          background,
          overflow: 'hidden',
          ...style
        }}
      >
        {/* scale=0 на первом рендере (до замера ResizeObserver) — прячем
            канвас чтобы не было flash с нативным размером 720×1280.
            Абсолютное центрирование через top/left 50% + translate. */}
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          width, height,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center center',
          visibility: scale > 0 ? 'visible' : 'hidden'
        }}>
          {children}
        </div>
      </div>
    </TimelineContext.Provider>
  );
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}

interface SpriteValue {
  localTime: number;
  progress: number;
  duration: number;
  visible: boolean;
}
const SpriteContext = React.createContext<SpriteValue>({ localTime: 0, progress: 0, duration: 0, visible: false });
export const useSprite = () => React.useContext(SpriteContext);

interface SpriteProps {
  start?: number;
  end?: number;
  keepMounted?: boolean;
  children: React.ReactNode | ((v: SpriteValue) => React.ReactNode);
}

// Sprite — рендерится в окне [start, end]. Дочерним компонентам доступен
// localTime (секунды с start) и progress (0..1) через useSprite.
export function Sprite({ start = 0, end = Infinity, children, keepMounted = false }: SpriteProps) {
  const { time } = useTimeline();
  const visible = time >= start && time <= end;
  if (!visible && !keepMounted) return null;
  const duration = end - start;
  const localTime = Math.max(0, time - start);
  const progress = duration > 0 && isFinite(duration)
    ? Math.min(Math.max(localTime / duration, 0), 1)
    : 0;
  const value: SpriteValue = { localTime, progress, duration, visible };
  return (
    <SpriteContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </SpriteContext.Provider>
  );
}
